import { useEffect, useState } from "react";
import { ShieldAlert, Loader2 } from "lucide-react";
import api, { formatApiError, onStepUpRequired } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Mounted once, app-wide. The axios layer asks for a re-auth whenever the server answers
 * 403 step_up_required; we prove it's really you, then the original request is retried.
 */
export function StepUpGate() {
  const [req, setReq] = useState(null);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => onStepUpRequired((detail) => {
    setValue("");
    setError("");
    setReq(detail);
  }), []);

  if (!req) return null;
  const needsTotp = req.needs_totp;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/step-up", {
        action: req.action,
        ...(needsTotp ? { code: value } : { password: value }),
      });
      const resolve = req.resolve;
      setReq(null);
      resolve(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    const resolve = req.resolve;
    setReq(null);
    resolve(false);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && cancel()}>
      <DialogContent className="sm:max-w-md" data-testid="step-up-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-primary" /> Confirm it's you
          </DialogTitle>
          <DialogDescription>{req.message}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="stepup">{needsTotp ? "Code from your authenticator app" : "Your password"}</Label>
            <Input id="stepup" data-testid="step-up-input" autoFocus value={value}
              type={needsTotp ? "text" : "password"}
              autoComplete={needsTotp ? "one-time-code" : "current-password"}
              onChange={(e) => setValue(e.target.value)} placeholder={needsTotp ? "123456" : ""} required />
          </div>
          {error && <p className="text-sm text-destructive" data-testid="step-up-error">{error}</p>}
          <p className="text-xs text-muted-foreground">
            One confirmation covers the next 10 minutes of this session.
          </p>
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="flex-1" onClick={cancel} data-testid="step-up-cancel">Cancel</Button>
            <Button type="submit" className="flex-1" disabled={busy} data-testid="step-up-submit">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirm
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
